/**
 * 단일 HTTP 클라이언트(spec 028 FR-001~004) — 도메인 파사드는 이 모듈의 `http` 만 쓰고 axios 를 직접 import 하지 않는다.
 *
 * - base URL `VITE_API_BASE_URL`(기본 `/api` — dev 는 Vite 프록시, 운영은 same-origin), 쿠키 세션이라 `withCredentials`.
 * - 모든 오류는 응답 인터셉터 한 곳에서 `ApiError{status, message}` 로 바뀌어 reject 된다(서버 `message` 우선).
 * - 오류 토스트는 여기서 최대 1회 — 화면은 같은 오류를 다시 띄우지 않는다. 요청별로 `silent`·`silentStatuses`·`silentWhen` 으로 억제.
 * - 무음이 아닌 401 은 로그인으로 보낸다(현재 경로를 redirect 로).
 */
import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

import { ROUTES } from '@/config/routes';
import { withRedirect } from '@/lib/authRedirect';
import { FRICTION_SESSION_HEADER, frictionSessionId } from '@/lib/frictionSession';
import { notify } from '@/lib/toast';

/** API 오류 — HTTP status + 서버 메시지(ApiError{status,message} 규격과 동일 형태). 응답 없음은 status 0. */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** 오류가 ApiError 이고(status 지정 시) 해당 HTTP 상태인지 — 화면 분기용 단일 판별. */
export function isApiStatus(error: unknown, status?: number): error is ApiError {
  return error instanceof ApiError && (status === undefined || error.status === status);
}

/** 요청 단위 오류 표시 옵션. */
export interface RequestOptions {
  /** 모든 오류 무음(토스트·401 이동 없음). */
  silent?: boolean;
  /** 지정 상태만 무음 — 화면이 인라인으로 처리하는 상태. */
  silentStatuses?: readonly number[];
  /** 술어 무음. */
  silentWhen?: (error: ApiError) => boolean;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    errorOptions?: RequestOptions;
  }
}

const DEFAULT_MESSAGE: Record<number, string> = {
  400: '요청 내용을 확인해 주세요.',
  401: '로그인이 필요합니다.',
  403: '접근 권한이 없습니다.',
  404: '요청한 정보를 찾을 수 없습니다.',
  409: '이미 처리된 요청입니다.',
  429: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  500: '서버 처리 중 오류가 발생했습니다.',
  503: '서비스를 잠시 사용할 수 없습니다.',
};
const NETWORK_MESSAGE = '서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.';

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  withCredentials: true,
  timeout: 30_000,
});

// 막힘 지점 측정 세션(specs/071) — 서버 기록이 같은 탭 세션 키를 쓰게 모든 요청에 싣는다(비식별, 해시로만 저장)
http.interceptors.request.use((config) => {
  const session = frictionSessionId();
  if (session) config.headers.set(FRICTION_SESSION_HEADER, session);
  return config;
});

function toApiError(error: AxiosError<{ message?: string }>): ApiError {
  const status = error.response?.status ?? 0;
  if (status === 0) return new ApiError(0, NETWORK_MESSAGE);
  const message =
    error.response?.data?.message ?? DEFAULT_MESSAGE[status] ?? '요청을 처리하지 못했습니다.';
  return new ApiError(status, message);
}

function isSilent(error: ApiError, options: RequestOptions | undefined): boolean {
  if (!options) return false;
  if (options.silent) return true;
  if (options.silentStatuses?.includes(error.status)) return true;
  return options.silentWhen?.(error) ?? false;
}

http.interceptors.response.use(
  (response) => response,
  (raw: AxiosError<{ message?: string }>) => {
    if (axios.isCancel(raw)) return Promise.reject(raw);
    const error = toApiError(raw);
    if (!isSilent(error, raw.config?.errorOptions)) {
      if (error.status === 401) {
        const here = `${window.location.pathname}${window.location.search}`;
        window.location.assign(withRedirect(ROUTES.login, here));
      } else {
        notify.error(error.message);
      }
    }
    return Promise.reject(error);
  },
);

/** 요청 옵션 헬퍼 — `http.get(url, withErrors({ silentStatuses: [404] }))`. */
export function withErrors(
  errorOptions: RequestOptions,
  config?: AxiosRequestConfig,
): AxiosRequestConfig {
  return { ...config, errorOptions };
}
